import {
  Navigate,
  Outlet,
  RouterProvider,
  createBrowserRouter,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense, type ReactElement } from "react";
import { defaultLanguage } from "@/lib/shared";
import { AppProviders } from "./AppProviders";
import MainShellLayout from "./layouts/MainShellLayout";
import MainHomePage from "./pages/MainHomePage";
import LoginPageRoute from "./pages/LoginPageRoute";
import RegisterPageRoute from "./pages/RegisterPageRoute";
import LocaleCatchAllPage from "./pages/LocaleCatchAllPage";
import {
  FollowingFeedSkeleton,
  SchedulesListSkeleton,
  ComparisonIndexSkeleton,
  ComparisonDetailSkeleton,
  MeProfileSkeleton,
  SettingsPageSkeleton,
  RouteFallbackSkeleton,
} from "./components/shell/PageSkeletons";
import ApplyJobLayout from "./pages/ApplyJobLayout";
import ApplyJobIndexRedirect from "./pages/ApplyJobIndexRedirect";
import ScheduleDetailPageSkeleton from "@/components/skeletons/ScheduleDetailPageSkeleton";

const FollowingPage = lazy(() => import("./pages/FollowingPage"));
const SchedulesPage = lazy(() => import("./pages/SchedulesPage"));
const ComparisonIndexPage = lazy(() => import("./pages/ComparisonIndexPage"));
const ComparisonDetailPage = lazy(() => import("./pages/ComparisonDetailPage"));
const MeProfilePage = lazy(() => import("./pages/MeProfilePage"));
const SettingsLayoutPage = lazy(() => import("./pages/SettingsLayoutPage"));
const SettingsProfilePage = lazy(() => import("./pages/SettingsProfilePage"));
const SettingsSavedPage = lazy(() => import("./pages/SettingsSavedPage"));
const SettingsBlockedPage = lazy(() => import("./pages/SettingsBlockedPage"));
const SettingsAppliedJobsPage = lazy(() => import("./pages/SettingsAppliedJobsPage"));
const SettingsJobsPage = lazy(() => import("./pages/SettingsJobsPage"));
const SettingsJobWatchPage = lazy(() => import("./pages/SettingsJobWatchPage"));
const SettingsJobCandidatePage = lazy(() => import("./pages/SettingsJobCandidatePage"));
const SettingsServicesPage = lazy(() => import("./pages/SettingsServicesPage"));
const SettingsAboutPage = lazy(() => import("./pages/SettingsAboutPage"));
const SettingsContactPage = lazy(() => import("./pages/SettingsContactPage"));
const SettingsDeletePage = lazy(() => import("./pages/SettingsDeletePage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const HelpPage = lazy(() => import("./pages/HelpPage"));
const ApplyJobStartRoute = lazy(() => import("./pages/ApplyJobStartRoute"));
const ApplyJobExperienceRoute = lazy(() => import("./pages/ApplyJobExperienceRoute"));
const ApplyJobEducationSkillsLanguageRoute = lazy(
  () => import("./pages/ApplyJobEducationSkillsLanguageRoute"),
);
const ApplyJobVideosRoute = lazy(() => import("./pages/ApplyJobVideosRoute"));
const ApplyJobProjectRoute = lazy(() => import("./pages/ApplyJobProjectRoute"));
const ApplyJobSalaryRoute = lazy(() => import("./pages/ApplyJobSalaryRoute"));
const ApplyJobReviewRoute = lazy(() => import("./pages/ApplyJobReviewRoute"));
const ApplyJobLoginRoute = lazy(() => import("./pages/ApplyJobLoginRoute"));
const ApplyJobUnknownStepPage = lazy(() => import("./pages/ApplyJobUnknownStepPage"));
const WatchPageRoute = lazy(() => import("./pages/WatchPageRoute"));
const ScheduleDetailPageRoute = lazy(() => import("./pages/ScheduleDetailPageRoute"));
const PublicProfilePageRoute = lazy(() => import("./pages/PublicProfilePageRoute"));

const withSuspense = (node: ReactElement, fallback: ReactElement) => (
  <Suspense fallback={fallback}>{node}</Suspense>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

function RootLayout() {
  return (
    <AppProviders>
      <Outlet />
    </AppProviders>
  );
}

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      {
        path: "/",
        element: <Navigate to={`/${defaultLanguage}`} replace />,
      },
      {
        path: "/:locale",
        element: <Outlet />,
        children: [
          { path: "auth/login", element: <LoginPageRoute /> },
          { path: "auth/register", element: <RegisterPageRoute /> },
          {
            path: "apply-job",
            element: <ApplyJobLayout />,
            children: [
              {
                index: true,
                element: <ApplyJobIndexRedirect />,
              },
              {
                path: "start",
                element: withSuspense(
                  <ApplyJobStartRoute />,
                  <RouteFallbackSkeleton />,
                ),
              },
              {
                path: "experience",
                element: withSuspense(
                  <ApplyJobExperienceRoute />,
                  <RouteFallbackSkeleton />,
                ),
              },
              {
                path: "education-skills-language",
                element: withSuspense(
                  <ApplyJobEducationSkillsLanguageRoute />,
                  <RouteFallbackSkeleton />,
                ),
              },
              {
                path: "videos",
                element: withSuspense(
                  <ApplyJobVideosRoute />,
                  <RouteFallbackSkeleton />,
                ),
              },
              {
                path: "project",
                element: withSuspense(
                  <ApplyJobProjectRoute />,
                  <RouteFallbackSkeleton />,
                ),
              },
              {
                path: "salary",
                element: withSuspense(
                  <ApplyJobSalaryRoute />,
                  <RouteFallbackSkeleton />,
                ),
              },
              {
                path: "review",
                element: withSuspense(
                  <ApplyJobReviewRoute />,
                  <RouteFallbackSkeleton />,
                ),
              },
              {
                path: "login",
                element: withSuspense(
                  <ApplyJobLoginRoute />,
                  <RouteFallbackSkeleton />,
                ),
              },
              {
                path: "*",
                element: withSuspense(
                  <ApplyJobUnknownStepPage />,
                  <RouteFallbackSkeleton />,
                ),
              },
            ],
          },
          {
            element: <MainShellLayout />,
            children: [
              { index: true, element: <MainHomePage /> },
              {
                path: "watch/:videoId",
                element: withSuspense(
                  <WatchPageRoute />,
                  <RouteFallbackSkeleton />,
                ),
              },
              {
                path: "profiles/:profileId",
                element: withSuspense(
                  <PublicProfilePageRoute />,
                  <RouteFallbackSkeleton />,
                ),
              },
              {
                path: "schedules/:scheduleId",
                element: withSuspense(
                  <ScheduleDetailPageRoute />,
                  <ScheduleDetailPageSkeleton />,
                ),
              },
              {
                path: "following",
                element: withSuspense(
                  <FollowingPage />,
                  <FollowingFeedSkeleton />,
                ),
              },
              {
                path: "schedules",
                element: withSuspense(
                  <SchedulesPage />,
                  <SchedulesListSkeleton />,
                ),
              },
              {
                path: "comparison",
                element: withSuspense(
                  <ComparisonIndexPage />,
                  <ComparisonIndexSkeleton />,
                ),
              },
              {
                path: "comparison/:id",
                element: withSuspense(
                  <ComparisonDetailPage />,
                  <ComparisonDetailSkeleton />,
                ),
              },
              {
                path: "me",
                element: withSuspense(<MeProfilePage />, <MeProfileSkeleton />),
              },
              {
                path: "privacy",
                element: withSuspense(
                  <PrivacyPage />,
                  <RouteFallbackSkeleton />,
                ),
              },
              {
                path: "help",
                element: withSuspense(
                  <HelpPage />,
                  <RouteFallbackSkeleton />,
                ),
              },
              {
                path: "settings",
                element: withSuspense(
                  <SettingsLayoutPage />,
                  <SettingsPageSkeleton />,
                ),
                children: [
                  {
                    path: "jobs/:jobId/watch",
                    element: withSuspense(
                      <SettingsJobWatchPage />,
                      <RouteFallbackSkeleton />,
                    ),
                  },
                  {
                    path: "jobs/:jobId/candidates/:personalProfileId",
                    element: withSuspense(
                      <SettingsJobCandidatePage />,
                      <SettingsPageSkeleton />,
                    ),
                  },
                  {
                    path: "saved",
                    element: withSuspense(
                      <SettingsSavedPage />,
                      <SettingsPageSkeleton />,
                    ),
                  },
                  {
                    path: "blocked",
                    element: withSuspense(
                      <SettingsBlockedPage />,
                      <SettingsPageSkeleton />,
                    ),
                  },
                  {
                    path: "applied-jobs",
                    element: withSuspense(
                      <SettingsAppliedJobsPage />,
                      <SettingsPageSkeleton />,
                    ),
                  },
                  {
                    path: "jobs",
                    element: withSuspense(
                      <SettingsJobsPage />,
                      <SettingsPageSkeleton />,
                    ),
                  },
                  {
                    path: "services",
                    element: withSuspense(
                      <SettingsServicesPage />,
                      <SettingsPageSkeleton />,
                    ),
                  },
                  {
                    path: "about",
                    element: withSuspense(
                      <SettingsAboutPage />,
                      <SettingsPageSkeleton />,
                    ),
                  },
                  {
                    path: "contact",
                    element: withSuspense(
                      <SettingsContactPage />,
                      <SettingsPageSkeleton />,
                    ),
                  },
                  {
                    path: "delete",
                    element: withSuspense(
                      <SettingsDeletePage />,
                      <SettingsPageSkeleton />,
                    ),
                  },
                  {
                    index: true,
                    element: withSuspense(
                      <SettingsProfilePage />,
                      <SettingsPageSkeleton />,
                    ),
                  },
                ],
              },
              { path: "*", element: <LocaleCatchAllPage /> },
            ],
          },
        ],
      },
    ],
  },
]);

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}
